/**
 * Document Upload Page (Public)
 * =============================
 * Public page for applicants to upload requested documents.
 * No authentication required - secured by URL token.
 */

import React, { useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  FileText,
  Upload,
  CheckCircle2,
  AlertTriangle,
  Clock,
  Loader2,
  Shield,
  X,
  File,
} from 'lucide-react';
import { BRAND } from '../config/brand';
import { usePublicDocumentRequest, usePublicUpload } from '../hooks/useDocumentRequests';

// Simple styles object
const styles = {
  container: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #0f0f1a 0%, #1a1a2e 100%)',
    color: '#e5e7eb',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  header: {
    padding: '20px 24px',
    borderBottom: '1px solid rgba(255,255,255,0.1)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  logo: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    fontSize: '18px',
    fontWeight: 600,
  },
  main: {
    maxWidth: '600px',
    margin: '0 auto',
    padding: '40px 24px',
  },
  card: {
    background: 'rgba(255,255,255,0.05)',
    borderRadius: '16px',
    border: '1px solid rgba(255,255,255,0.1)',
    padding: '32px',
    marginBottom: '24px',
  },
  title: {
    fontSize: '24px',
    fontWeight: 600,
    marginBottom: '8px',
  },
  subtitle: {
    fontSize: '14px',
    color: '#9ca3af',
    marginBottom: '24px',
  },
  sectionLabel: {
    fontSize: '12px',
    color: '#6b7280',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginBottom: '12px',
  },
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 12px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: 500,
  },
  dropzone: {
    border: '2px dashed rgba(139, 92, 246, 0.4)',
    borderRadius: '12px',
    padding: '40px',
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'all 0.2s',
    marginBottom: '24px',
  },
  dropzoneHover: {
    borderColor: '#8b5cf6',
    background: 'rgba(139, 92, 246, 0.1)',
  },
  button: {
    padding: '12px 24px',
    borderRadius: '10px',
    border: 'none',
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    width: '100%',
  },
  buttonPrimary: {
    background: '#8b5cf6',
    color: 'white',
  },
  buttonDisabled: {
    background: '#374151',
    color: '#6b7280',
    cursor: 'not-allowed',
  },
};

const DOCUMENT_TYPE_LABELS = {
  proof_of_address: 'Proof of Address',
  bank_statement: 'Bank Statement',
  utility_bill: 'Utility Bill',
  payslip: 'Payslip',
  tax_return: 'Tax Return',
  source_of_funds: 'Source of Funds',
  additional_id: 'Additional ID',
  selfie: 'Selfie Photo',
  other: 'Other Document',
};

export default function DocumentUploadPage() {
  const { token } = useParams();
  const { data: request, isLoading, error } = usePublicDocumentRequest(token);
  const uploadMutation = usePublicUpload();

  const [selectedType, setSelectedType] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadedTypes, setUploadedTypes] = useState([]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      setSelectedFile(file);
    }
  }, []);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !selectedType) return;

    try {
      await uploadMutation.mutateAsync({
        token,
        file: selectedFile,
        documentType: selectedType,
      });

      // Track uploaded type
      setUploadedTypes((prev) => [...prev, selectedType]);

      // Reset for next upload
      setSelectedFile(null);
      setSelectedType('');
    } catch (err) {
      // Error handled by mutation
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div style={styles.container}>
        <div style={{ ...styles.main, textAlign: 'center', paddingTop: '100px' }}>
          <Loader2 size={40} style={{ animation: 'spin 1s linear infinite', marginBottom: '16px', color: '#8b5cf6' }} />
          <p>Loading document request...</p>
          <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div style={styles.container}>
        <header style={styles.header}>
          <div style={styles.logo}>
            <Shield size={24} style={{ color: '#8b5cf6' }} />
            {BRAND.name}
          </div>
        </header>
        <div style={styles.main}>
          <div style={{ ...styles.card, textAlign: 'center' }}>
            <AlertTriangle size={48} style={{ color: '#ef4444', marginBottom: '16px' }} />
            <h2 style={{ ...styles.title, marginBottom: '12px' }}>Link Not Valid</h2>
            <p style={{ color: '#9ca3af', lineHeight: 1.6 }}>
              {error.message || 'This document request link is invalid or has expired. Please contact the organization that sent you this link for a new one.'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Calculate remaining document types to upload
  const remainingTypes = request?.document_types?.filter((t) => !uploadedTypes.includes(t)) || [];
  const allUploaded = remainingTypes.length === 0 && request?.document_types?.length > 0;

  return (
    <div style={styles.container}>
      {/* Header */}
      <header style={styles.header}>
        <div style={styles.logo}>
          <Shield size={24} style={{ color: '#8b5cf6' }} />
          GetClearance
        </div>
      </header>

      <main style={styles.main}>
        {/* Title Card */}
        <div style={styles.card}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px', marginBottom: '24px' }}>
            <div
              style={{
                width: '48px',
                height: '48px',
                borderRadius: '12px',
                background: 'linear-gradient(135deg, #8b5cf6, #a855f7)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <FileText size={24} color="white" />
            </div>
            <div>
              <h1 style={styles.title}>Document Upload</h1>
              <p style={{ ...styles.subtitle, margin: 0 }}>
                {request?.tenant_name || 'An organization'} has requested documents from you
              </p>
            </div>
          </div>

          {/* Request details */}
          {request?.reason && (
            <div style={{ marginBottom: '16px' }}>
              <div style={styles.sectionLabel}>Reason</div>
              <p style={{ margin: 0, fontSize: '14px' }}>{request.reason}</p>
            </div>
          )}

          {request?.instructions && (
            <div style={{ marginBottom: '16px' }}>
              <div style={styles.sectionLabel}>Instructions</div>
              <p style={{ margin: 0, fontSize: '14px', lineHeight: 1.6 }}>{request.instructions}</p>
            </div>
          )}

          {/* Expiry notice */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '12px',
              background: 'rgba(245, 158, 11, 0.1)',
              borderRadius: '8px',
              fontSize: '13px',
              color: '#f59e0b',
            }}
          >
            <Clock size={16} />
            Link expires {new Date(request?.expires_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
          </div>
        </div>

        {/* Documents to Upload */}
        <div style={styles.card}>
          <div style={styles.sectionLabel}>Documents Requested</div>

          <div style={{ display: 'grid', gap: '12px', marginBottom: '24px' }}>
            {request?.document_types?.map((type) => {
              const isUploaded = uploadedTypes.includes(type);
              return (
                <div
                  key={type}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '14px 16px',
                    borderRadius: '10px',
                    background: isUploaded ? 'rgba(16, 185, 129, 0.1)' : 'rgba(255,255,255,0.05)',
                    border: `1px solid ${isUploaded ? 'rgba(16, 185, 129, 0.3)' : 'rgba(255,255,255,0.1)'}`,
                  }}
                >
                  <span style={{ fontWeight: 500 }}>{DOCUMENT_TYPE_LABELS[type] || type}</span>
                  {isUploaded ? (
                    <span
                      style={{
                        ...styles.badge,
                        background: 'rgba(16, 185, 129, 0.2)',
                        color: '#10b981',
                      }}
                    >
                      <CheckCircle2 size={14} />
                      Uploaded
                    </span>
                  ) : (
                    <span
                      style={{
                        ...styles.badge,
                        background: 'rgba(107, 114, 128, 0.2)',
                        color: '#9ca3af',
                      }}
                    >
                      <Clock size={14} />
                      Pending
                    </span>
                  )}
                </div>
              );
            })}
          </div>

          {/* All uploaded success message */}
          {allUploaded && (
            <div
              style={{
                textAlign: 'center',
                padding: '32px',
                background: 'rgba(16, 185, 129, 0.1)',
                borderRadius: '12px',
                border: '1px solid rgba(16, 185, 129, 0.3)',
              }}
            >
              <CheckCircle2 size={48} style={{ color: '#10b981', marginBottom: '16px' }} />
              <h3 style={{ margin: '0 0 8px', fontSize: '18px', fontWeight: 600 }}>All Documents Uploaded</h3>
              <p style={{ margin: 0, color: '#9ca3af', fontSize: '14px' }}>
                Thank you! All requested documents have been received.
              </p>
            </div>
          )}

          {/* Upload form - only show if there are remaining documents */}
          {!allUploaded && (
            <>
              {/* Document type selector */}
              <div style={{ marginBottom: '16px' }}>
                <div style={styles.sectionLabel}>Select Document Type</div>
                <select
                  value={selectedType}
                  onChange={(e) => setSelectedType(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '8px',
                    border: '1px solid rgba(255,255,255,0.1)',
                    background: 'rgba(255,255,255,0.05)',
                    color: 'white',
                    fontSize: '14px',
                  }}
                >
                  <option value="">Choose document type...</option>
                  {remainingTypes.map((type) => (
                    <option key={type} value={type}>
                      {DOCUMENT_TYPE_LABELS[type] || type}
                    </option>
                  ))}
                </select>
              </div>

              {/* Dropzone */}
              <div
                style={{
                  ...styles.dropzone,
                  ...(isDragging ? styles.dropzoneHover : {}),
                }}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => document.getElementById('file-input').click()}
              >
                <input
                  id="file-input"
                  type="file"
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                  accept=".pdf,.jpg,.jpeg,.png"
                />

                {selectedFile ? (
                  <div>
                    <File size={32} style={{ color: '#8b5cf6', marginBottom: '12px' }} />
                    <p style={{ margin: '0 0 4px', fontWeight: 500 }}>{selectedFile.name}</p>
                    <p style={{ margin: 0, fontSize: '13px', color: '#9ca3af' }}>
                      {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedFile(null);
                      }}
                      style={{
                        marginTop: '12px',
                        padding: '6px 12px',
                        background: 'rgba(239, 68, 68, 0.2)',
                        border: 'none',
                        borderRadius: '6px',
                        color: '#ef4444',
                        cursor: 'pointer',
                        fontSize: '13px',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                      }}
                    >
                      <X size={14} />
                      Remove
                    </button>
                  </div>
                ) : (
                  <div>
                    <Upload size={32} style={{ color: '#8b5cf6', marginBottom: '12px' }} />
                    <p style={{ margin: '0 0 4px', fontWeight: 500 }}>
                      Drop your file here or click to browse
                    </p>
                    <p style={{ margin: 0, fontSize: '13px', color: '#9ca3af' }}>
                      PDF, JPG, or PNG up to 10MB
                    </p>
                  </div>
                )}
              </div>

              {/* Error message */}
              {uploadMutation.error && (
                <div
                  style={{
                    padding: '12px',
                    background: 'rgba(239, 68, 68, 0.1)',
                    border: '1px solid rgba(239, 68, 68, 0.3)',
                    borderRadius: '8px',
                    marginBottom: '16px',
                    fontSize: '13px',
                    color: '#ef4444',
                  }}
                >
                  {uploadMutation.error.message}
                </div>
              )}

              {/* Upload button */}
              <button
                onClick={handleUpload}
                disabled={!selectedFile || !selectedType || uploadMutation.isPending}
                style={{
                  ...styles.button,
                  ...(!selectedFile || !selectedType || uploadMutation.isPending
                    ? styles.buttonDisabled
                    : styles.buttonPrimary),
                }}
              >
                {uploadMutation.isPending ? (
                  <>
                    <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload size={16} />
                    Upload Document
                  </>
                )}
              </button>
            </>
          )}
        </div>

        {/* Security notice */}
        <div
          style={{
            textAlign: 'center',
            fontSize: '12px',
            color: '#6b7280',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '6px',
          }}
        >
          <Shield size={14} />
          {BRAND.sdk.securedBy}. Your documents are encrypted and protected.
        </div>
      </main>
    </div>
  );
}
